import CrossReference from "@/components/links/CrossReference/CrossReference";
import List from "@/components/visualizations/List/List";
import useTranslation from "@/hooks/useTranslation";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import {
  formatAmountWithUnit,
  formatPackagingCategories,
  formatSupplier,
} from "@/pages/dashboard/utils/formatters";
import { Segmented } from "antd";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import { MaterialGroupBy } from "../../hooks/useMaterialImpact";
import { ImpactMetric } from "../../utils/types";
import { usePackagingImpactPerMaterial } from "@/pages/dashboard/hooks/usePackagingImpact";

const step = 5;

export default function PackagingImpactTopContributors({ metric }: { metric: ImpactMetric }) {
  const [limit, setLimit] = useState(step);
  const { startDate, endDate } = useDateRange();
  const translation = useTranslation();
  const [groupBy, setGroupBy] = useState<MaterialGroupBy>("name");

  useEffect(() => {
    setLimit(step);
  }, [startDate, endDate]);

  const { data, isLoading, isFetching } = usePackagingImpactPerMaterial({
    groupBy,
    pagination: { page: 1, pageSize: limit + 5 },
    sort: { key: metric.key, order: "descend" },
    metric: metric.key,
  });

  const numMore = Math.min((data?.data.length || 0) - limit);

  const listData = data?.data
    ? data?.data.slice(0, limit).map((row) => {
        const supplier =
          row.suppliers.length === 1
            ? formatSupplier(row.suppliers[0].name, row.suppliers[0].address)
            : row.suppliers.length + " suppliers";

        return {
          label:
            groupBy === "id" ? (
              <CrossReference to={`/matching/packaging/materials/${row.packaging_material_id}`}>
                {row.packaging_material_name}
              </CrossReference>
            ) : (
              row.packaging_material_name
            ),
          value: row.impact[metric.key],
          type: formatPackagingCategories(row.category),
          subType: supplier,
          extraProp: {
            label: "Amount",
            value: formatAmountWithUnit(row.quantity, row.unit, 0),
          },
        };
      })
    : [];

  return (
    <Widget
      title={"Top contributing materials"}
      tooltip={translation["dashboard"]["packaging"]["top_contributors"]}
      topRight={
        <Segmented
          value={groupBy}
          onChange={setGroupBy}
          options={[
            { label: "Unique", value: "id" },
            { label: "Grouped", value: "name" },
          ]}
        />
      }
    >
      <List
        loading={isLoading || isFetching}
        data={{
          list: listData,
          total: data?.total_material_impact[metric.key],
        }}
        loadMore={{
          show: numMore > 0,
          numberOfMore: numMore,
          fn: () => setLimit(limit + step),
        }}
        format={metric.formatter}
      />
    </Widget>
  );
}
